import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Trash2, Plus, Wand2, CheckCircle2 } from 'lucide-react';

const MANUAL = '__manual__';
const TYPES = ['text', 'textarea', 'date', 'checkbox', 'yesno', 'radio', 'select', 'signature', 'declaration', 'instruction', 'attachment', 'table'];

function ConfidenceBadge({ c }) {
  if (c === null || c === undefined) return null;
  const pct = Math.round(c * 100);
  const cls = pct >= 80 ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
    : pct >= 55 ? 'bg-amber-100 text-amber-700 border-amber-300'
    : 'bg-red-100 text-red-700 border-red-300';
  return <Badge className={`border text-[10px] ${cls}`} data-testid="field-confidence">{pct}% match</Badge>;
}

export default function TemplateMappingDialog({ open, onOpenChange, template, onSaved }) {
  const { token, API } = useContext(AuthContext);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [mdFields, setMdFields] = useState([]);
  const [fields, setFields] = useState([]);
  const [name, setName] = useState('');
  const [orgName, setOrgName] = useState('');
  const [signatures, setSignatures] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/application-templates/md-fields`, auth).then((r) => setMdFields(r.data.fields || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open || !template) return;
    setFields((template.fields || []).map((f) => ({ ...f })));
    setName(template.name || '');
    setOrgName(template.organisation_name || '');
    setSignatures(template.signatures || []);
    setAttachments(template.attachments || []);
  }, [open, template?.id]);

  const grouped = useMemo(() => {
    const g = {};
    mdFields.forEach((f) => { (g[f.group] = g[f.group] || []).push(f); });
    return g;
  }, [mdFields]);

  const setField = (idx, patch) => setFields((fs) => fs.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  const removeField = (idx) => setFields((fs) => fs.filter((_, i) => i !== idx));
  const addField = () => setFields((fs) => [...fs, { id: `new_${Date.now()}`, label: 'New field', type: 'text', md_field: null, manual_entry: true, required: false, default_value: '', options: [] }]);

  const save = async (activate) => {
    if (!name.trim()) { toast.error('Give the template a name'); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        organisation_name: orgName.trim(),
        fields: fields.map((f) => ({
          id: f.id, label: f.label, type: f.type,
          md_field: f.md_field || null, manual_entry: !f.md_field,
          required: !!f.required, default_value: f.default_value || null,
          confidence: f.confidence ?? null, options: f.options || [],
          acrofield_name: f.acrofield_name || null, position: f.position || null,
          date_format: f.date_format || null,
        })),
        signatures, attachments,
        status: activate ? 'active' : 'mapping',
      };
      await axios.put(`${API}/application-templates/${template.id}`, payload, auth);
      toast.success(activate ? 'Template saved & activated' : 'Mappings saved');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save template');
    } finally { setSaving(false); }
  };

  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="template-mapping-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wand2 className="w-5 h-5 text-teal-600" /> Map Form Fields</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            We analysed <strong>{template.source_filename || template.name}</strong> and suggested a Memorial Desk field for
            each detected field. Confirm or change each mapping, mark required fields, then activate the template so it can be
            used on applications.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Template name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="template-name-input" />
          </div>
          <div>
            <Label className="text-xs">Organisation</Label>
            <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} data-testid="template-org-input" />
          </div>
        </div>

        <div className="rounded-md border divide-y" data-testid="template-fields-list">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            <div className="col-span-4">Form field</div>
            <div className="col-span-4">Maps to Memorial Desk field</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-2 text-right">Required</div>
          </div>
          {fields.map((f, idx) => (
            <div key={f.id || idx} className="grid grid-cols-12 gap-2 px-3 py-2 items-center" data-testid={`template-field-row-${idx}`}>
              <div className="col-span-4">
                <Input className="h-8 text-sm" value={f.label} onChange={(e) => setField(idx, { label: e.target.value })} data-testid={`field-label-${idx}`} />
                <div className="flex items-center gap-1.5 mt-1">
                  <ConfidenceBadge c={f.confidence} />
                  {f.acrofield_name && <span className="text-[10px] text-slate-400">PDF: {f.acrofield_name}</span>}
                </div>
              </div>
              <div className="col-span-4">
                <Select value={f.md_field || MANUAL} onValueChange={(v) => setField(idx, { md_field: v === MANUAL ? null : v, manual_entry: v === MANUAL })}>
                  <SelectTrigger className="h-8 text-sm" data-testid={`field-mapping-${idx}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value={MANUAL}>✎ Manual entry (ask the user)</SelectItem>
                    {Object.entries(grouped).map(([group, list]) => (
                      <div key={group}>
                        <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400">{group}</div>
                        {list.map((mf) => <SelectItem key={mf.key} value={mf.key}>{mf.label}</SelectItem>)}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
                {(!f.md_field) && (
                  <Input className="h-7 text-xs mt-1" placeholder="Default value (optional)" value={f.default_value || ''} onChange={(e) => setField(idx, { default_value: e.target.value })} data-testid={`field-default-${idx}`} />
                )}
              </div>
              <div className="col-span-2">
                <Select value={f.type} onValueChange={(v) => setField(idx, { type: v })}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`field-type-${idx}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2 flex items-center justify-end gap-2">
                <Checkbox checked={!!f.required} onCheckedChange={(v) => setField(idx, { required: !!v })} data-testid={`field-required-${idx}`} />
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => removeField(idx)} data-testid={`field-remove-${idx}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={addField} data-testid="add-field-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add missing field</Button>

        {(signatures.length > 0 || attachments.length > 0) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {signatures.length > 0 && (
              <div className="rounded-md border p-3">
                <p className="text-xs font-semibold text-slate-600 uppercase mb-1">Signatures required</p>
                {signatures.map((s, i) => (
                  <label key={i} className="flex items-center gap-2 text-sm py-0.5">
                    <Checkbox checked={!!s.required} onCheckedChange={(v) => setSignatures((ss) => ss.map((x, j) => j === i ? { ...x, required: !!v } : x))} />
                    {s.role.replace(/_/g, ' ')}
                  </label>
                ))}
              </div>
            )}
            {attachments.length > 0 && (
              <div className="rounded-md border p-3">
                <p className="text-xs font-semibold text-slate-600 uppercase mb-1">Required attachments</p>
                {attachments.map((a, i) => (
                  <label key={i} className="flex items-center gap-2 text-sm py-0.5">
                    <Checkbox checked={!!a.required} onCheckedChange={(v) => setAttachments((aa) => aa.map((x, j) => j === i ? { ...x, required: !!v } : x))} />
                    {a.type.replace(/_/g, ' ')}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="save-mappings-btn">Save Draft</Button>
          <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => save(true)} disabled={saving} data-testid="activate-template-btn">
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> {saving ? 'Saving…' : 'Save & Activate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
